"use client";

import { useLocale, useTranslations } from "next-intl";
import { Link, usePathname, useRouter } from "@/i18n/navigation";
import {
  useId,
  useLayoutEffect,
  useState,
  type ComponentProps,
  type ReactNode,
} from "react";
import { cn } from "@/lib/utils";
import { ThemeToggle } from "./theme-toggle";
import { Logomark } from "./Logomark";
import { routing } from "@/i18n/routing";

const navLinkClass =
  "font-display text-base font-medium tracking-tight text-[var(--on-surface-variant)] hover:text-[var(--primary)] transition-colors py-3 px-4 rounded-lg hover:bg-[var(--surface-container-high)] min-h-11 flex items-center";
const navLinkClassActive =
  "font-display text-base font-medium tracking-tight text-[var(--on-surface)] border-l-4 border-[var(--primary)] pl-3 pr-4 py-3 min-h-11 flex items-center";

type NavKey = "services" | "caseStudies" | "aiLab" | "methodology" | "contact";

function useActiveNavKey(): NavKey | null {
  const pathname = usePathname();
  const [hash, setHash] = useState("");

  useLayoutEffect(() => {
    const read = () => setHash(window.location.hash);
    read();
    window.addEventListener("hashchange", read);
    return () => window.removeEventListener("hashchange", read);
  }, [pathname]);

  if (pathname.startsWith("/services")) return "services";
  if (pathname.startsWith("/case-studies")) return "caseStudies";
  if (pathname.startsWith("/contact")) return "contact";

  if (pathname === "/" || pathname === "") {
    if (hash === "#ai-lab") return "aiLab";
    if (hash === "#methodology") return "methodology";
    return null;
  }

  return null;
}

const desktopBase =
  "font-display text-base xl:text-lg font-medium tracking-tight transition-colors px-0 pb-1 border-b-2 border-transparent";

function desktopLinkClass(isActive: boolean) {
  return cn(
    desktopBase,
    isActive
      ? "text-[var(--on-surface)] border-[color-mix(in_srgb,var(--primary)_82%,var(--design-accent-pop)_18%)]"
      : "text-[var(--on-surface-variant)] hover:text-[var(--primary)] hover:border-[color-mix(in_srgb,var(--primary)_78%,var(--design-accent-pop)_22%)]",
  );
}

/** Inline SVG flags; emoji regional indicators often fail on Windows (letters/empty boxes). */
function FlagSv({ className }: { className?: string }) {
  return (
    <svg
      data-theme-preserve-colors
      className={className}
      viewBox="0 0 16 10"
      width={14}
      height={9}
      aria-hidden
      xmlns="http://www.w3.org/2000/svg"
    >
      <rect width="16" height="10" fill="#006AA7" />
      <path fill="#FECC00" d="M6 0h2v10H6zM0 4h16v2H0z" />
    </svg>
  );
}

function FlagGb({ className }: { className?: string }) {
  const uid = useId().replace(/:/g, "");
  const clipId = `uk-clip-${uid}`;
  return (
    <svg
      data-theme-preserve-colors
      className={className}
      viewBox="0 0 60 30"
      width={17}
      height={9}
      aria-hidden
      xmlns="http://www.w3.org/2000/svg"
    >
      <defs>
        <clipPath id={clipId}>
          <path d="M0 0v30h60V0z" />
        </clipPath>
      </defs>
      <path fill="#012169" d="M0 0h60v30H0z" />
      <path stroke="#fff" strokeWidth={6} d="M0 0l60 30M60 0L0 30" />
      <path
        stroke="#C8102E"
        strokeWidth={4}
        clipPath={`url(#${clipId})`}
        d="M0 0l60 30M60 0L0 30"
      />
      <path stroke="#fff" strokeWidth={10} d="M30 0v30M0 15h60" />
      <path stroke="#C8102E" strokeWidth={6} d="M30 0v30M0 15h60" />
    </svg>
  );
}

function localeFlag(loc: (typeof routing.locales)[number]): ReactNode {
  const wrap =
    "rounded-[2px] ring-1 ring-[color-mix(in_srgb,var(--outline-variant)_22%,transparent)]";
  if (loc === "sv") return <FlagSv className={wrap} />;
  return <FlagGb className={wrap} />;
}

function hrefHasFragment(href: string): boolean {
  return href.includes("#");
}

/**
 * Mobile menu: plain `<Link>` can leave the viewport scrolled mid-page after route
 * changes (especially with `<details>` + iOS). Programmatic `push` with `scroll: true`
 * plus an explicit scroll for non-hash routes matches user expectation of a new page.
 */
function MobileNavLink({
  href,
  onMenuClose,
  className,
  children,
  ...props
}: Omit<ComponentProps<typeof Link>, "onClick" | "href"> & {
  href: string;
  onMenuClose?: () => void;
}) {
  const router = useRouter();

  return (
    <Link
      href={href}
      className={className}
      {...props}
      onClick={(e) => {
        if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
        if (e.button !== 0) return;
        const targetAttr = e.currentTarget.getAttribute("target");
        if (targetAttr && targetAttr !== "_self") return;

        e.preventDefault();
        onMenuClose?.();
        router.push(href, { scroll: !hrefHasFragment(href) });

        if (!hrefHasFragment(href)) {
          const scrollToTop = () => {
            window.scrollTo({ top: 0, left: 0, behavior: "instant" });
            document.documentElement.scrollTop = 0;
            document.body.scrollTop = 0;
          };
          requestAnimationFrame(() => {
            requestAnimationFrame(scrollToTop);
          });
        }
      }}
    >
      {children}
    </Link>
  );
}

function LangSwitch({ className }: { className?: string }) {
  const locale = useLocale();
  const pathname = usePathname();
  const t = useTranslations("nav");
  /** Track: p-0.5 + gap-0.5 → 0.375rem horizontal trim; thumb aligns with flex halves */
  const thumbBase =
    "pointer-events-none absolute top-0.5 bottom-0.5 w-[calc((100%-0.375rem)/2)] rounded-[var(--radius-sm)] bg-[var(--surface)] shadow-[0_0_24px_rgba(225,225,247,0.08)] ring-1 ring-[color-mix(in_srgb,var(--outline-variant)_18%,transparent)] motion-safe:transition-[left] motion-safe:duration-300 motion-safe:ease-[var(--ease-out-quint)]";
  const linkClass =
    "relative z-10 inline-flex min-h-8 flex-1 items-center justify-center gap-0.5 sm:gap-1 rounded-[var(--radius-sm)] px-1.5 sm:px-2 text-[length:var(--label-md)] font-normal uppercase tracking-[0.1em] transition-colors duration-200 touch-manipulation text-[var(--on-surface-variant)] hover:text-[var(--on-surface)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color-mix(in_srgb,var(--primary)_70%,var(--design-accent-pop)_30%)] focus-visible:ring-offset-1 focus-visible:ring-offset-[color-mix(in_srgb,var(--surface-container-high)_88%,var(--design-ambient-tint)_12%)] data-[active=true]:text-[var(--on-surface)]";

  return (
    <div
      className={cn(
        "relative inline-flex min-w-0 shrink-0 items-stretch rounded-[var(--radius-sm)] border border-[color-mix(in_srgb,var(--outline-variant)_18%,transparent)] bg-[color-mix(in_srgb,var(--surface-container-high)_82%,var(--design-ambient-tint)_18%)] p-0.5 gap-0.5",
        className,
      )}
      style={{ fontFamily: "var(--font-body)" }}
      role="group"
      aria-label={t("langSwitch")}
    >
      <span
        aria-hidden
        className={thumbBase}
        style={{
          left:
            locale === "sv"
              ? "0.125rem"
              : "calc((100% - 0.375rem) / 2 + 0.25rem)",
        }}
      />
      {routing.locales.map((loc) => (
        <Link
          key={loc}
          href={pathname}
          locale={loc}
          prefetch={false}
          data-active={loc === locale ? "true" : "false"}
          className={linkClass}
          aria-label={loc === "sv" ? t("langSvAria") : t("langEnAria")}
          aria-current={loc === locale ? "true" : undefined}
        >
          <span className="inline-flex shrink-0 items-center justify-center leading-none" aria-hidden>
            {localeFlag(loc)}
          </span>
          <span className="hidden sm:inline">{loc === "sv" ? t("langSv") : t("langEn")}</span>
        </Link>
      ))}
    </div>
  );
}

export function Nav() {
  const active = useActiveNavKey();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const t = useTranslations("nav");

  return (
    <nav
      className="fixed top-0 left-0 right-0 z-50 border-b border-[var(--outline)]"
      style={{
        background: "rgba(255, 255, 255, 0.92)",
        backdropFilter: "blur(20px)",
      }}
    >
      <div className="site-section-x">
        <div className="site-container flex items-center justify-between gap-3 py-3 sm:py-5 min-h-[3.5rem] sm:min-h-0">
          <div className="flex shrink-0 items-center">
            <Logomark size="nav" />
          </div>

          <div className="hidden lg:flex items-center gap-4 xl:gap-8">
            <div className="flex items-center gap-6 xl:gap-10">
              <Link
                href="/services"
                className={desktopLinkClass(active === "services")}
                style={{ fontFamily: "var(--font-display)" }}
              >
                {t("services")}
              </Link>
              <Link
                href="/case-studies"
                className={desktopLinkClass(active === "caseStudies")}
                style={{ fontFamily: "var(--font-display)" }}
              >
                {t("caseStudies")}
              </Link>
              <Link
                href="/#ai-lab"
                scroll={false}
                className={desktopLinkClass(active === "aiLab")}
                style={{ fontFamily: "var(--font-display)" }}
              >
                {t("aiLab")}
              </Link>
              <Link
                href="/#methodology"
                scroll={false}
                className={desktopLinkClass(active === "methodology")}
                style={{ fontFamily: "var(--font-display)" }}
              >
                {t("methodology")}
              </Link>
              <Link
                href="/contact"
                className={desktopLinkClass(active === "contact")}
                style={{ fontFamily: "var(--font-display)" }}
              >
                {t("contact")}
              </Link>
            </div>
          </div>

          <div className="flex items-center gap-2 sm:gap-3 shrink-0">
            <div className="hidden lg:flex items-center gap-2 sm:gap-3">
              <ThemeToggle />
              <LangSwitch />
            </div>
            <Link
              href="/contact"
              className="inline-flex shrink-0 items-center justify-center bg-[var(--primary)] text-[var(--on-primary)] h-11 min-h-11 px-2.5 sm:px-4 rounded-[var(--radius-sm)] font-medium text-xs sm:text-sm hover:opacity-90 active:scale-[0.99] transition-all touch-manipulation whitespace-nowrap lg:h-auto lg:min-h-0 lg:px-5 lg:py-2.5"
              style={{ fontFamily: "var(--font-body)" }}
            >
              {t("meetUs")}
            </Link>

            <div className="hidden md:flex lg:hidden items-center gap-1">
              <Link
                href="/services"
                className={cn(
                  "text-xs font-medium px-0 py-2 touch-manipulation border-b-2 border-transparent transition-colors",
                  active === "services"
                    ? "text-[var(--on-surface)] border-[var(--primary)]"
                    : "text-[var(--on-surface-variant)] hover:text-[var(--primary)] hover:border-[var(--primary)]",
                )}
                style={{ fontFamily: "var(--font-body)" }}
              >
                {t("services")}
              </Link>
              <Link
                href="/case-studies"
                className={cn(
                  "text-xs font-medium px-0 py-2 touch-manipulation border-b-2 border-transparent transition-colors",
                  active === "caseStudies"
                    ? "text-[var(--on-surface)] border-[var(--primary)]"
                    : "text-[var(--on-surface-variant)] hover:text-[var(--primary)] hover:border-[var(--primary)]",
                )}
                style={{ fontFamily: "var(--font-body)" }}
              >
                {t("caseStudies")}
              </Link>
              <Link
                href="/contact"
                className={cn(
                  "text-xs font-medium px-0 py-2 touch-manipulation border-b-2 border-transparent transition-colors",
                  active === "contact"
                    ? "text-[var(--on-surface)] border-[var(--primary)]"
                    : "text-[var(--on-surface-variant)] hover:text-[var(--primary)] hover:border-[var(--primary)]",
                )}
                style={{ fontFamily: "var(--font-body)" }}
              >
                {t("contact")}
              </Link>
            </div>

            <details
              className="lg:hidden relative group"
              open={mobileMenuOpen}
              onToggle={(event) => {
                setMobileMenuOpen(event.currentTarget.open);
              }}
            >
              <summary
                className="list-none cursor-pointer flex items-center justify-center size-11 rounded-lg border border-[var(--outline)] bg-[var(--surface)] text-[var(--on-surface)] hover:bg-[var(--surface-container-low)] touch-manipulation [&::-webkit-details-marker]:hidden"
                aria-label={mobileMenuOpen ? t("closeMenu") : t("openMenu")}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
                  <path d="M4 6h16M4 12h16M4 18h16" strokeLinecap="round" />
                </svg>
              </summary>
              <div
                className="absolute right-0 top-full mt-2 w-[min(calc(100vw-2rem),18rem)] rounded-lg border border-[var(--outline)] bg-[var(--surface)] shadow-lg py-2 z-50"
                style={{ boxShadow: "0 12px 40px rgba(45, 48, 83, 0.12)" }}
              >
                <MobileNavLink
                  href="/services"
                  className={active === "services" ? navLinkClassActive : navLinkClass}
                  style={{ fontFamily: "var(--font-display)" }}
                  onMenuClose={() => setMobileMenuOpen(false)}
                >
                  {t("services")}
                </MobileNavLink>
                <MobileNavLink
                  href="/case-studies"
                  className={active === "caseStudies" ? navLinkClassActive : navLinkClass}
                  style={{ fontFamily: "var(--font-display)" }}
                  onMenuClose={() => setMobileMenuOpen(false)}
                >
                  {t("caseStudies")}
                </MobileNavLink>
                <MobileNavLink
                  href="/#ai-lab"
                  className={active === "aiLab" ? navLinkClassActive : navLinkClass}
                  style={{ fontFamily: "var(--font-display)" }}
                  onMenuClose={() => setMobileMenuOpen(false)}
                >
                  {t("aiLab")}
                </MobileNavLink>
                <MobileNavLink
                  href="/#methodology"
                  className={active === "methodology" ? navLinkClassActive : navLinkClass}
                  style={{ fontFamily: "var(--font-display)" }}
                  onMenuClose={() => setMobileMenuOpen(false)}
                >
                  {t("methodology")}
                </MobileNavLink>
                <MobileNavLink
                  href="/contact"
                  className={active === "contact" ? navLinkClassActive : navLinkClass}
                  style={{ fontFamily: "var(--font-display)" }}
                  onMenuClose={() => setMobileMenuOpen(false)}
                >
                  {t("contact")}
                </MobileNavLink>
                <div className="px-4 py-3 border-t border-[var(--outline)] flex items-center gap-3">
                  <ThemeToggle />
                  <LangSwitch className="min-w-0 flex-1" />
                </div>
              </div>
            </details>
          </div>
        </div>
      </div>
    </nav>
  );
}
